<!-- Hero Section -->
<section class="relative py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
  <div class="absolute inset-0 opacity-10">
    <div class="absolute inset-0" style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');"></div>
  </div>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <div class="text-center text-white">
      <h1 class="text-4xl md:text-5xl font-bold mb-6">Blog & Insights</h1>
      <p class="text-xl text-white/90 max-w-3xl mx-auto">
        Expert insights, tutorials, and best practices from our team to help you succeed in digital transformation.
      </p>
    </div>
  </div>
</section>

<!-- Featured Post -->
<section class="py-16 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="bg-gradient-to-br from-primary-50 to-primary-100 rounded-3xl overflow-hidden shadow-xl">
      <div class="grid lg:grid-cols-2 gap-0">
        <div class="h-64 lg:h-auto">
          <img [src]="featuredPost.image" [alt]="featuredPost.title" class="w-full h-full object-cover">
        </div>
        <div class="p-8 lg:p-12 flex flex-col justify-center">
          <div class="flex items-center gap-3 mb-4">
            <span class="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Featured</span>
            <span class="text-primary-600 font-medium text-sm">{{ featuredPost.category }}</span>
          </div>
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{{ featuredPost.title }}</h2>
          <p class="text-gray-600 mb-6">{{ featuredPost.excerpt }}</p>
          <div class="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
            <span>{{ featuredPost.date }}</span>
            <span>•</span>
            <span>{{ featuredPost.author }}</span>
            <span>•</span>
            <span>{{ featuredPost.readTime }}</span>
          </div>
          <a [routerLink]="['/blog', featuredPost.slug]" class="btn-primary self-start">
            Read Full Article
          </a>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Category Filter -->
<section class="py-8 bg-gray-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex flex-wrap justify-center gap-3">
      @for (category of categories; track category) {
        <button
          (click)="filterByCategory(category)"
          [class]="selectedCategory === category ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-primary-50 hover:text-primary-600'"
          class="px-6 py-2.5 rounded-full font-medium transition-all duration-200 shadow-sm">
          {{ category }}
        </button>
      }
    </div>
  </div>
</section>

<!-- Blog Posts Grid -->
<section class="py-16 bg-gray-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (post of filteredPosts; track post.slug) {
        <article class="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
          <!-- Cover Image -->
          <div class="h-52 overflow-hidden">
            <img [src]="post.image" [alt]="post.title" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500">
          </div>
          <div class="p-6">
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                {{ post.category }}
              </span>
              <span class="text-sm text-gray-500">{{ post.readTime }}</span>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
              {{ post.title }}
            </h3>
            <p class="text-gray-600 mb-4 line-clamp-2">{{ post.excerpt }}</p>
            <div class="flex items-center justify-between pt-4 border-t border-gray-100">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {{ getInitials(post.author) }}
                </div>
                <div>
                  <span class="text-sm font-medium text-gray-900">{{ post.author }}</span>
                  <p class="text-xs text-gray-500">{{ post.date }}</p>
                </div>
              </div>
              <a [routerLink]="['/blog', post.slug]" class="text-primary-600 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                Learn More
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
          </div>
        </article>
      }
    </div>
  </div>
</section>

<!-- Newsletter Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
    <h2 class="text-3xl md:text-4xl font-bold mb-6">Subscribe to Our Newsletter</h2>
    <p class="text-xl text-white/90 mb-8">
      Get the latest insights and updates delivered straight to your inbox.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
      <input
        type="email"
        placeholder="Enter your email"
        class="flex-1 px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-300">
      <button class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
        Subscribe
      </button>
    </div>
  </div>
</section>
